import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'umi';
// 导入图标资源
import iconSortAsc from '@/pages/aigc/assets/svg/icon-sort-asc.svg';
import iconSortDesc from '@/pages/aigc/assets/svg/icon-sort-desc.svg';
import iconCopy from '@/pages/aigc/assets/svg/icon-copy-new.svg';
import iconPaste from '@/pages/aigc/assets/svg/icon-insert-left.svg';
import iconInsertLeft from '@/pages/aigc/assets/svg/icon-paste.svg';
import iconInsertRight from '@/pages/aigc/assets/svg/icon-arrow-right.svg';
import iconClear from '@/pages/aigc/assets/svg/icon-clear.svg';
import iconDelete from '@/pages/aigc/assets/svg/icon-delete.svg';
import iconAi from '@/pages/aigc/assets/svg/agent-ai-icon.svg';
import iconModifyCondition from '@/pages/aigc/assets/svg/icon-modify-condition.svg';
import iconAdjustFormat from '@/pages/aigc/assets/svg/icon-adjust-format.svg';
import iconInsertRowAbove from '@/pages/aigc/assets/svg/icon-insert-row-above.svg';
import iconInsertRowBelow from '@/pages/aigc/assets/svg/icon-insert-row-below.svg';
import './index.scss';

/** 菜单状态 */
export interface IMenuState {
  /** 是否可见 */
  visible: boolean;
  /** X 坐标 */
  x: number;
  /** Y 坐标 */
  y: number;
  /** 菜单类型：行、列或单元格 */
  type: 'row' | 'column' | 'cell';
  /** 选中的行/列索引数组（支持多选） */
  selectedIndices: number[];
  /** 选中的单元格坐标（仅单元格类型有效） */
  cellCoords?: { row: number; col: number; };
}

export interface IContextMenuProps {
  /** 菜单状态 */
  menuState: IMenuState;
  /** 关闭菜单 */
  onClose: () => void;
  /** 复制 */
  onCopy: () => void;
  /** 粘贴 */
  onPaste: () => void;
  /** 在上方插入行 */
  onInsertRowAbove: (count: number) => void;
  /** 在下方插入行 */
  onInsertRowBelow: (count: number) => void;
  /** 清除行内容 */
  onClearRowContent: () => void;
  /** 删除行 */
  onDeleteRow: () => void;
  /** 升序排序 */
  onSortAsc: () => void;
  /** 倒序排序 */
  onSortDesc: () => void;
  /** 在左侧插入列 */
  onInsertColumnLeft: (count: number) => void;
  /** 在右侧插入列 */
  onInsertColumnRight: (count: number) => void;
  /** 清除列内容 */
  onClearColumnContent: () => void;
  /** 删除列 */
  onDeleteColumn: () => void;
  /** AI 修改本列数据条件 */
  onAIModifyCondition: () => void;
  /** AI 调整数据格式 */
  onAIAdjustFormat: () => void;
  /** 清除单元格内容 */
  onClearCellContent?: () => void;
  /** 当前列是否支持排序（仅列菜单有效） */
  isSortable?: boolean;
  /** 是否禁用插入行操作（达到最大行数时禁用） */
  disableInsertRow?: boolean;
  /** 选中的行/列数量（用于菜单显示） */
  selectedCount?: number;
}

/** 插入行/列输入组件 */
interface IInsertInputProps {
  icon: React.ReactNode;
  label: string;
  unit: string;
  onConfirm: (count: number) => void;
  /** 是否禁用 */
  disabled?: boolean;
}

const InsertInput: React.FC<IInsertInputProps> = memo(({ icon, label, unit, onConfirm, disabled = false }: any) => {
  const [count, setCount] = useState(1);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      // 超过 999 自动设置为 999
      setCount(Math.min(value, 999));
    }
  }, []);

  const handleClick = useCallback(() => {
    if (!disabled) {
      onConfirm(count);
    }
  }, [count, onConfirm, disabled]);

  // 处理回车键确认
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !disabled) {
      e.preventDefault();
      e.stopPropagation();
      onConfirm(count);
    }
  }, [count, onConfirm, disabled]);

  return (
    <div 
      className={`dba-table-context-menu__item dba-table-context-menu__item--with-input${disabled ? ' dba-table-context-menu__item--disabled' : ''}`} 
      onClick={handleClick}
    >
      <span className="dba-table-context-menu__item-icon">{icon}</span>
      <span className="dba-table-context-menu__item-label">{label}</span>
      <input
        type="number"
        className="dba-table-context-menu__input"
        value={count}
        min={1}
        max={999}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
      <span className="dba-table-context-menu__item-unit">{unit}</span>
    </div>
  );
});

InsertInput.displayName = 'InsertInput';

/** 菜单项组件 - 使用图片图标 */
interface IMenuItemProps {
  icon?: React.ReactNode;
  iconSrc?: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
  sub?: boolean;
}

const MenuItem: React.FC<IMenuItemProps> = memo(({ icon, iconSrc, label, onClick, danger, sub }: any) => (
  <div
    className={`dba-table-context-menu__item${danger ? ' dba-table-context-menu__item--danger' : ''}${sub ? ' dba-table-context-menu__item--sub' : ''}`}
    onClick={onClick}
  >
    <span className="dba-table-context-menu__item-icon">
      {iconSrc ? (
        <img src={iconSrc} alt="" className="dba-table-context-menu__item-img" />
      ) : (
        icon
      )}
    </span>
    <span className="dba-table-context-menu__item-label">{label}</span>
  </div>
));

MenuItem.displayName = 'MenuItem';

/**
 * 表格右键菜单组件
 */
const TableContextMenu: React.FC<IContextMenuProps> = memo(({
  menuState,
  onClose,
  onCopy,
  onPaste,
  onInsertRowAbove,
  onInsertRowBelow,
  onClearRowContent,
  onDeleteRow,
  onSortAsc,
  onSortDesc,
  onInsertColumnLeft,
  onInsertColumnRight,
  onClearColumnContent,
  onDeleteColumn,
  onAIModifyCondition,
  onAIAdjustFormat,
  onClearCellContent,
  // isSortable = true, // 默认支持排序
  disableInsertRow = false, // 默认不禁用插入行
  selectedCount = 1, // 选中的列/行数量
}: any) => {
  // 是否为单选（多行/多列选中时隐藏 AI 操作项："修改本列数据条件" 和 "调整数据格式"）
  const isSingleSelection = selectedCount <= 1;
  const { formatMessage } = useIntl();
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // 计算菜单位置，确保不超出视口
  useEffect(() => {
    if (!menuState.visible || !menuRef.current) return;

    const rect = menuRef.current.getBoundingClientRect();
    const { innerWidth: vw, innerHeight: vh } = window;
    const x = Math.min(menuState.x, vw - rect.width - 8);
    const y = Math.min(menuState.y, vh - rect.height - 8);
    setPosition({ x, y });
  }, [menuState.visible, menuState.x, menuState.y]);

  // 点击外部或按 ESC 关闭菜单
  useEffect(() => {
    if (!menuState.visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuState.visible, onClose]);

  if (!menuState.visible) return null;

  const isRow = menuState.type === 'row';
  const isColumn = menuState.type === 'column';
  const isCell = menuState.type === 'cell';

  const menuContent = (
    <div ref={menuRef} className="dba-table-context-menu" style={{ left: position.x, top: position.y }}>
      {/* 单元格菜单 */}
      {isCell && (
        <>
          <MenuItem iconSrc={iconCopy} label={formatMessage({ id: 'dba_smart_table_copy' })} onClick={onCopy} />
          <MenuItem iconSrc={iconPaste} label={formatMessage({ id: 'dba_smart_table_paste' })} onClick={onPaste} />
          <div className="dba-table-context-menu__divider" />
          <MenuItem iconSrc={iconClear} label={formatMessage({ id: 'dba_smart_table_clear_content' })} onClick={onClearCellContent || onClearRowContent} />
        </>
      )}

      {/* 列菜单 */}
      {isColumn && (
        <>
          {/* AI 帮手：仅单列选中时显示（多列选中时隐藏"修改本列数据条件"和"调整数据格式"） */}
          {isSingleSelection && (
            <>
              <div className="dba-table-context-menu__section">
                <div className="dba-table-context-menu__section-header">
                  <img src={iconAi} alt="" className="dba-table-context-menu__ai-icon" />
                  <span>{formatMessage({ id: 'dba_smart_table_ai_help' })}</span>
                </div>
                <MenuItem iconSrc={iconModifyCondition} label={formatMessage({ id: 'dba_smart_table_modify_column_condition_label' })} onClick={onAIModifyCondition} sub />
                <MenuItem iconSrc={iconAdjustFormat} label={formatMessage({ id: 'dba_smart_table_adjust_format_label' })} onClick={onAIAdjustFormat} sub />
              </div>
              <div className="dba-table-context-menu__divider" />
            </>
          )}
          {/* 排序菜单始终显示，不受多选状态影响 */}
          <MenuItem iconSrc={iconSortAsc} label={formatMessage({ id: 'dba_smart_table_sort_asc' })} onClick={onSortAsc} />
          <MenuItem iconSrc={iconSortDesc} label={formatMessage({ id: 'dba_smart_table_sort_desc' })} onClick={onSortDesc} />
          <div className="dba-table-context-menu__divider" />
          <MenuItem iconSrc={iconCopy} label={formatMessage({ id: 'dba_smart_table_copy' })} onClick={onCopy} />
          <MenuItem iconSrc={iconPaste} label={formatMessage({ id: 'dba_smart_table_paste' })} onClick={onPaste} />
          <div className="dba-table-context-menu__divider" />
          <InsertInput icon={<img src={iconInsertLeft} alt="" className="dba-table-context-menu__item-img" />} label={formatMessage({ id: 'dba_smart_table_insert_left' })} unit={formatMessage({ id: 'dba_smart_table_column' })} onConfirm={onInsertColumnLeft} />
          <InsertInput icon={<img src={iconInsertRight} alt="" className="dba-table-context-menu__item-img" />} label={formatMessage({ id: 'dba_smart_table_insert_right' })} unit={formatMessage({ id: 'dba_smart_table_column' })} onConfirm={onInsertColumnRight} />
          <div className="dba-table-context-menu__divider" />
          <MenuItem iconSrc={iconClear} label={formatMessage({ id: 'dba_smart_table_clear_content' })} onClick={onClearColumnContent} />
          <MenuItem iconSrc={iconDelete} label={formatMessage({ id: 'dba_smart_table_delete_column' })} onClick={onDeleteColumn} />
        </>
      )}

      {/* 行菜单 */}
      {isRow && (
        <>
          <MenuItem iconSrc={iconCopy} label={formatMessage({ id: 'dba_smart_table_copy' })} onClick={onCopy} />
          <MenuItem iconSrc={iconPaste} label={formatMessage({ id: 'dba_smart_table_paste' })} onClick={onPaste} />
          <div className="dba-table-context-menu__divider" />
          <InsertInput icon={<img src={iconInsertRowAbove} alt="" className="dba-table-context-menu__item-img" />} label={formatMessage({ id: 'dba_smart_table_insert_above' })} unit={formatMessage({ id: 'dba_smart_table_row' })} onConfirm={onInsertRowAbove} disabled={disableInsertRow} />
          <InsertInput icon={<img src={iconInsertRowBelow} alt="" className="dba-table-context-menu__item-img" />} label={formatMessage({ id: 'dba_smart_table_insert_below' })} unit={formatMessage({ id: 'dba_smart_table_row' })} onConfirm={onInsertRowBelow} disabled={disableInsertRow} />
          <div className="dba-table-context-menu__divider" />
          <MenuItem iconSrc={iconClear} label={formatMessage({ id: 'dba_smart_table_clear_content' })} onClick={onClearRowContent} />
          <MenuItem iconSrc={iconDelete} label={formatMessage({ id: 'dba_smart_table_delete_row' })} onClick={onDeleteRow} />
        </>
      )}
    </div>
  );

  return createPortal(menuContent, document.body);
});

TableContextMenu.displayName = 'TableContextMenu';

export default TableContextMenu;
